import { useWindowSize } from 'react-use';
import { useFirm, useFirmBranding } from '../../../../Firm';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useFirmClient } from '../../../../FirmClient';
import type { Firm, FirmClient } from '../../../../generated/graphql';
import {
  ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL,
  getVisibleScorecardElementSymbols,
  SCORECARD_ASSETS_ELEMENT_SYMBOLS,
  SCORECARD_CASH_FLOW_ELEMENT_SYMBOLS,
  SCORECARD_ELEMENT_SYMBOLS,
  SCORECARD_RISK_ELEMENT_SYMBOLS,
  ScorecardElementSymbol,
  useScorecard,
} from '../../../../Scorecard';
import { useFeatureFlagsContext } from '../../../../FeatureFlags';
import { PresentationViewOptions } from '../types';
import { DEFAULT_PRESENTATION_VIEW_OPTIONS } from '../constants';
import { getHeadsOfHousehold, usePersons } from '../../../../Person';
import { isElementGroupFocused } from '../util';
import { useBtDetails } from '../../../../BtDetails';
import { formatEstateStatusWeight, useEstateStatusReadOnlyHouseholdSettings } from '../../../../EstateStatus';

export interface UseFullScreenScorecardPageArgs {
  firmID: string;
  clientID: string;
  householdID: string;
  pathName?: string;
  fullScreenRegex?: RegExp;
  initialFirm?: Pick<
    Firm,
    'elementCustomizationBetaEnabled' | 'elementCustomizations' | 'estateStatusBetaEnabled' | 'entitlements'
  > | null;
  initialFirmClient?: FirmClient | null;
  applyFirmElementVisibility?: boolean;
}

export function getFullScreenScorecardVisibleElements({
  allowHouseholdElementVisibility = false,
  allowEstateStatus,
  applyFirmElementVisibility,
  firm,
  firmClient,
}: {
  allowHouseholdElementVisibility?: boolean;
  allowEstateStatus: boolean;
  applyFirmElementVisibility: boolean;
  firm: Pick<Firm, 'elementCustomizationBetaEnabled' | 'elementCustomizations'> | null | undefined;
  firmClient?: Pick<FirmClient, 'elementVisibilityOverrides'> | null | undefined;
}) {
  const routeVisibleElements = SCORECARD_ELEMENT_SYMBOLS.filter(
    (symbol) => symbol !== ESTATE_STATUS_SCORECARD_ELEMENT_SYMBOL || allowEstateStatus === true
  );

  if (!applyFirmElementVisibility || firm?.elementCustomizationBetaEnabled !== true) return routeVisibleElements;

  return getVisibleScorecardElementSymbols({
    allowFirmClientVisibilityOverrides: allowHouseholdElementVisibility,
    firm,
    firmClient,
    symbols: routeVisibleElements,
  });
}

export const useFullScreenScorecardPage = ({
  firmID,
  clientID,
  householdID,
  pathName,
  fullScreenRegex,
  initialFirm = null,
  initialFirmClient = null,
  applyFirmElementVisibility = false,
}: UseFullScreenScorecardPageArgs) => {
  const { width, height } = useWindowSize();
  const { allowEstateStatus, allowHouseholdElementVisibility } = useFeatureFlagsContext();

  const { firmClient: queriedFirmClient } = useFirmClient({ firmClientID: clientID, firmID });
  const { firm: queriedFirm, firmQuery } = useFirm(firmID);
  const firm = queriedFirm ?? initialFirm;
  const firmClient = queriedFirmClient ?? initialFirmClient;
  const { scorecard, scorecardQuery } = useScorecard({ householdID });
  const { estateStatusHouseholdSettings } = useEstateStatusReadOnlyHouseholdSettings(
    allowEstateStatus ? householdID : undefined
  );
  const { btDetails } = useBtDetails({ householdID, includeBusinesses: true });
  const { firmBranding, firmBrandingQuery } = useFirmBranding(firmID);
  const { persons } = usePersons({ householdID });

  const firmHasNoLogo = !firmBrandingQuery.isLoading && !firmBranding?.logo;
  const headsOfHousehold = getHeadsOfHousehold(persons);

  const scorecardRef = useRef<HTMLDivElement>(null);
  const firmLogoImageRef = useRef<HTMLImageElement>(null);
  const fullPresentationRef = useRef<HTMLDivElement>(null);
  const [scaleFactor, setScaleFactor] = useState<number>();
  const [viewOptions, setViewOptions] = useState<PresentationViewOptions>(DEFAULT_PRESENTATION_VIEW_OPTIONS);
  const isFirmCustomizationLoading = applyFirmElementVisibility && !firm && firmQuery.isLoading;
  const visibleElements = useMemo(() => {
    if (isFirmCustomizationLoading) return [];

    return getFullScreenScorecardVisibleElements({
      allowHouseholdElementVisibility,
      allowEstateStatus,
      applyFirmElementVisibility,
      firm,
      firmClient,
    });
  }, [
    allowEstateStatus,
    allowHouseholdElementVisibility,
    applyFirmElementVisibility,
    firm,
    firmClient,
    isFirmCustomizationLoading,
  ]);
  const visibleCashFlowElements = useMemo(
    () => SCORECARD_CASH_FLOW_ELEMENT_SYMBOLS.filter((symbol) => visibleElements.includes(symbol)),
    [visibleElements]
  );
  const visibleAssetElements = useMemo(
    () => SCORECARD_ASSETS_ELEMENT_SYMBOLS.filter((symbol) => visibleElements.includes(symbol)),
    [visibleElements]
  );
  const visibleRiskElements = useMemo(
    () => SCORECARD_RISK_ELEMENT_SYMBOLS.filter((symbol) => visibleElements.includes(symbol)),
    [visibleElements]
  );

  const allFocused =
    viewOptions.focusedElements?.length === visibleElements.length &&
    visibleElements.every((element) => viewOptions.focusedElements?.includes(element));
  const cashFlowFocused = isElementGroupFocused(viewOptions.focusedElements, visibleCashFlowElements);
  const assetsFocused = isElementGroupFocused(viewOptions.focusedElements, visibleAssetElements);
  const riskFocused = isElementGroupFocused(viewOptions.focusedElements, visibleRiskElements);
  const [shortcutsEnabled, setShortcutsEnabled] = useState(true);
  const estateStatusChecklistItems = estateStatusHouseholdSettings?.checklist?.map((item) => ({
    id: item.id,
    label: item.label,
    state: item.state,
    weightLabel: formatEstateStatusWeight(item.weight),
  }));

  useEffect(() => {
    if (fullScreenRegex && pathName) {
      setShortcutsEnabled(fullScreenRegex.test(pathName));
    }
  }, [pathName, fullScreenRegex]);

  useEffect(() => {
    setViewOptions((prev) => {
      const currentFocused = (prev.focusedElements ?? visibleElements).filter((element) =>
        visibleElements.includes(element)
      );
      const nextFocused = currentFocused.length ? currentFocused : visibleElements;

      if (
        currentFocused.length === prev.focusedElements?.length &&
        nextFocused.length === prev.focusedElements?.length &&
        nextFocused.every((element, index) => prev.focusedElements?.[index] === element)
      ) {
        return prev;
      }

      return { ...prev, focusedElements: nextFocused };
    });
  }, [visibleElements]);

  function modifyViewOptions(options: Partial<PresentationViewOptions>) {
    setViewOptions((prev) => ({ ...prev, ...options }));
  }

  function toggleShowEmojis() {
    modifyViewOptions({ showEmojis: !viewOptions.showEmojis });
  }

  function toggleShowTargetScores() {
    modifyViewOptions({ showTargetScores: !viewOptions.showTargetScores });
  }

  function toggleShowClientName() {
    modifyViewOptions({ showClientName: !viewOptions.showClientName });
  }

  function toggleShowClientAge() {
    modifyViewOptions({ showClientAge: !viewOptions.showClientAge });
  }

  function toggleShowFinancialDetails() {
    modifyViewOptions({
      showFinancialDetails: !viewOptions.showFinancialDetails,
    });
  }

  function toggleElementVisibility(element: ScorecardElementSymbol) {
    if (!visibleElements.includes(element)) return;

    modifyViewOptions({
      focusedElements: viewOptions.focusedElements?.includes(element)
        ? viewOptions.focusedElements.filter((e) => e !== element)
        : [...(viewOptions.focusedElements ?? []), element],
    });
  }

  function focusElementVisibility(element: ScorecardElementSymbol) {
    if (!visibleElements.includes(element)) return;

    modifyViewOptions({
      focusedElements:
        viewOptions.focusedElements?.includes(element) && viewOptions.focusedElements.length === 1 ? [] : [element],
    });
  }

  function toggleAllElementsVisibility() {
    modifyViewOptions({
      focusedElements: allFocused ? [] : visibleElements,
    });
  }

  function toggleCashFlowElementsVisibility() {
    modifyViewOptions({
      focusedElements: cashFlowFocused ? visibleElements : visibleCashFlowElements,
    });
  }

  function toggleAssetsElementsVisibility() {
    modifyViewOptions({
      focusedElements: assetsFocused ? visibleElements : visibleAssetElements,
    });
  }

  function toggleRiskElementsVisibility() {
    modifyViewOptions({
      focusedElements: riskFocused ? visibleElements : visibleRiskElements,
    });
  }

  useEffect(() => {
    setScaleFactor(Math.min(width / 1920, height / 1080));
  }, [width, height]);

  return {
    btDetails,
    householdID,
    firm,
    isFirmCustomizationLoading,
    firmHasNoLogo,
    scorecard,
    scorecardQuery,
    scaleFactor,
    firmBranding,
    firmBrandingQuery,
    fullPresentationRef,
    scorecardRef,
    firmLogoImageRef,
    firmClient,
    visibleElements,
    viewOptions,
    headsOfHousehold,
    toggleShowClientName,
    toggleShowClientAge,
    toggleElementVisibility,
    toggleAllElementsVisibility,
    toggleCashFlowElementsVisibility,
    toggleAssetsElementsVisibility,
    toggleRiskElementsVisibility,
    allFocused,
    cashFlowFocused,
    riskFocused,
    assetsFocused,
    toggleShowFinancialDetails,
    toggleShowEmojis,
    toggleShowTargetScores,
    focusElementVisibility,
    shortcutsEnabled,
    allowEstateStatus,
    estateStatusChecklistItems,
  };
};
